<script setup lang="ts">
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { DETAIL_HEADER_OPTION, PAGE_CONFIG, PageType } from '../../constants';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useInventoryDashboardStore } from '../useInventoryDashboardStore';
import InventoryDashboardChildLayout from '../InventoryDashboardChildLayout.vue';
import { DetailAggregateBusinessData, DetailBaseBusinessData } from '../useInventoryDashboardStore/types';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { InventoryDashboardViewModel } from '../inventoryDashboardViewModel';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import ArrowIcon from '@/corePagesModule/BaseTableContent/components/ArrowIcon.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { HeaderList, isTableType } from '@/coreDomainsModule/table';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';

const t = i18n.t;
const route = useRoute();

const store = useInventoryDashboardStore();
const { getDetailAggregateBusinessData, getDetailBaseBusinessData } = store;
const vm = new InventoryDashboardViewModel();

const loadingStatus = ref<LoadingStatus>({ type: 'loading' });
const sortType = ref<'ASC' | 'DESC'>('DESC');
const sortTarget = ref<string | undefined>(undefined);
const pagingOptions = ref<IPagingOptions>(initPagingOptions(30));
const filterItems = ref<FilterContentItem[]>([]);
const isExcludeNoAccuracy = ref(false);
const DetailAggregateBusinessData = ref<DetailAggregateBusinessData[]>([]);
const DetailBaseBusinessData = ref<DetailBaseBusinessData[]>();
const checkedBusinessId = ref<string[]>([]);
const displayYearMonth = ref<IYearMonth>(DateService.getShiftedIYearMonthFromNowDateByMonths(0));

const tableRef = ref<HTMLElement | null>(null);
const tableWidthInfo = ref<Record<string, number | undefined>>({});

const locale = computed(() => i18n.locale);

const pageType = computed((): PageType => {
  const curRoute = route.path.split('/').pop();
  if (!curRoute) throw new Error();
  switch (curRoute) {
    case 'stock':
      return PageType.Stock;
    case 'forecast-accuracy':
      return PageType.ForecastAccuracy;
  }
  throw new Error();
});

const pageConfig = computed(() => {
  if (!pageType.value) return { title: 'エラー', desc: '' };
  return PAGE_CONFIG[pageType.value] ?? { title: 'エラー', desc: '' };
});

const headerOption = computed(() => DETAIL_HEADER_OPTION[pageType.value]);
const fullHeaderKeys = computed(() => headerOption.value.map((h) => h.key));
const tableType = computed(() => {
  const rawTableType = 'inventoryDashboard' + pageType.value.charAt(0).toUpperCase() + pageType.value.slice(1);

  if (!isTableType(rawTableType)) {
    console.error(`Invalid table type: ${rawTableType}`);
    throw new Error(`Invalid table type: ${rawTableType}`);
  }
  return rawTableType;
});

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({ tableType: tableType, widthInfo: tableWidthInfo });
const { onMousedownResizer } = useColumnResizer({
  tableType: tableType,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const modifiableHeaders = computed(
  (): HeaderList =>
    headerOption.value
      .filter((h) => !['checkbox', 'detail', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: vm.convertHeaderKeyToDisplayValue(h.key),
        isShown: headerList.value.includes(h.key),
      }))
);

const gridTemplateColumns = computed(() =>
  headerOption.value
    .filter((header) => headerList.value.includes(header.key))
    .map((h) => {
      const saved = tableWidthInfo.value[h.key];
      return saved ? `${saved}px` : `minmax(${h.width}px, ${h.width}fr)`;
    })
);
/**
 * データフェッチ
 */
const fetch = async () => {
  if (pageType.value === PageType.Stock) {
    await _fetchAggregateBusinessData();
  } else if (pageType.value === PageType.ForecastAccuracy) {
    await _fetchBaseBusinessData();
  }
};

/**
 * 集計データフェッチ
 */
const _fetchAggregateBusinessData = async () => {
  loadingStatus.value.type = 'loading';
  const res = await getDetailAggregateBusinessData(
    sortType.value,
    sortTarget.value,
    pagingOptions.value.pageSize,
    pagingOptions.value.offset,
    filterItems.value
  );
  if (res === null) {
    loadingStatus.value.type = 'error';
    return;
  }
  DetailAggregateBusinessData.value = res[0];
  pagingOptions.value = res[1];
  loadingStatus.value.type = res[0].length === 0 ? 'zero-match' : 'success';
};

/**
 * 関連取引データフェッチ
 */
const _fetchBaseBusinessData = async () => {
  loadingStatus.value.type = 'loading';
  const res = await getDetailBaseBusinessData(
    displayYearMonth.value,
    sortType.value,
    sortTarget.value,
    pagingOptions.value.pageSize,
    pagingOptions.value.offset,
    isExcludeNoAccuracy.value,
    filterItems.value
  );
  if (res === null) {
    loadingStatus.value.type = 'error';
    return;
  }
  DetailBaseBusinessData.value = res[0];
  pagingOptions.value = res[1];
  loadingStatus.value.type = res[0].length === 0 ? 'zero-match' : 'success';
};

const isBaseBusiness = computed(() => pageType.value === PageType.ForecastAccuracy);

/**
 * ヘッダーキーをソート用のキーに変換する
 * @param headerKey - ヘッダーキー
 */
const convertHeaderKeyToSortKey = (headerKey: string) => {
  switch (headerKey) {
    case 'latestInventoryAmount':
      return 'currentInventory';
    case 'lastMonthEndInventoryAmount':
      return 'endInventory';
    case 'lastMonthSalesResultAmount':
    case 'salesResultAmount':
      return 'salesResult';
    case 'lastMonthCustomerForecastAmount':
    case 'customerForecastAmount':
      return 'customerForecast';
    default:
      return headerKey;
  }
};
/**
 * ソートの矢印を押した時のハンドラ
 * @param headerKey - ヘッダーキー
 */
const onClickSortArrowHandler = async (headerKey: string) => {
  const sortKey = convertHeaderKeyToSortKey(headerKey);
  if (sortTarget.value === sortKey) {
    sortType.value = sortType.value === 'ASC' ? 'DESC' : 'ASC';
  } else {
    sortTarget.value = sortKey;
    sortType.value = 'ASC';
  }
  pagingOptions.value = initPagingOptions(pagingOptions.value.pageSize);
  await fetch();
};

/**
 * ページング変更時のハンドラ
 * @param option - pagingOption
 */
const onChangePagingHandler = async (option: IPagingOptions) => {
  console.log(option);
  pagingOptions.value = { ...option };
  await fetch();
};

/**
 * フィルター適用時のハンドラ
 * @param changedFilterItems - 変更したフィルタ情報
 */
const onApplyFilterHandler = async (changedFilterItems: FilterContentItem[]) => {
  filterItems.value = [...changedFilterItems];
  await fetch();
};

/**
 * 内示正答率がない取引を除外するかどうかのチェックボックス操作時のハンドラ
 * @param isChecked - チェックされたかどうか
 */
const onApplyExcludeNoAccuracy = async (isChecked: boolean) => {
  isExcludeNoAccuracy.value = isChecked;
  await fetch();
};

/**
 * チェックされたかどうか
 * @param businessId - 対象の取引ID
 * @param isChecked - チェック済みかどうか
 */
const onCheckedRow = (businessId: string, isChecked: boolean) => {
  if (isChecked && !checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value.push(businessId);
  } else if (!isChecked && checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value = checkedBusinessId.value.filter((b) => b !== businessId);
  }
};

/**
 * ヘッダーを切り替える処理
 * @param headerKey - ヘッダーキー
 * @param isHide - 隠すかどうか
 */
const onToggleHeader = async (headerKey: string, isHide: boolean) => {
  await updateDisplayHeader(headerKey, isHide);
};

/**
 * 表示月を変更した際の処理
 * @param changed - 変更後の表示月
 */
const onChangeDisplayYearMonth = async (changed: IYearMonth) => {
  displayYearMonth.value = changed;
  await fetch();
};

onMounted(async () => {
  await fetch();
});
</script>
<template>
  <InventoryDashboardChildLayout>
    <div id="overview-list-detail" class="body">
      <div class="body__header">
        <div class="body__header__desc">
          <div class="body__header__title">
            <Mdi v-if="pageConfig.icon" :icon="pageConfig.icon.name" :variant="pageConfig.icon.variant" :style="pageConfig.icon.style" />
            <div class="body__header__title--text">{{ t(pageConfig.title) }}</div>
          </div>
          <div class="body__header__desc">{{ pageConfig.desc && t(pageConfig.desc) }}</div>
        </div>
        <div class="body__header__control">
          <div class="body__header__control__filter">
            <FilterModalButton
              :filter-modal-type="isBaseBusiness ? 'inventoryDashboardDetailBase' : 'inventoryDashboardDetailAgg'"
              :select-input-index="10"
              :filter-items="filterItems"
              @apply="onApplyFilterHandler"
            />
            <template v-if="pageType === 'forecastAccuracy'">
              <div class="body__header__control__filter--month-picker">
                {{ t('表示月') }}
                <MonthPicker
                  class="body__header__control__filter--month-picker__input"
                  :model-value="displayYearMonth"
                  :locale="locale"
                  :prop-format="t('MonthPickerFormat')"
                  @update:model-value="onChangeDisplayYearMonth"
                />
              </div>
              <RowCheckBox
                :label-text="t('内示正答率がない取引を非表示にする')"
                class="body__header__control__filter--checkbox"
                @change-status="onApplyExcludeNoAccuracy"
              />
            </template>
          </div>
          <Pagination
            class="inventory-header__pagination"
            :paging-options="pagingOptions ?? initPagingOptions(0)"
            :is-loading="loadingStatus.type === 'loading'"
            no-bottom-margin
            @on-change-page="onChangePagingHandler($event)"
          />
        </div>
      </div>
      <div class="body__table base-table-wrapper">
        <EzTable
          ref="tableRef"
          :grid-template-columns="gridTemplateColumns"
          :loading-status="loadingStatus"
          sticky-header
          is-vertical-scrollable
          class="body__table__container"
        >
          <template #header>
            <template v-for="(header, index) in headerList.slice(0, -1)" :key="index">
              <EzHeader v-if="header === 'checkbox'" class="body__table__header"><RowCheckBox /></EzHeader>
              <EzHeader v-else-if="header === 'detail'" :content-padding="0" force-center class="body__table__header" />
              <EzHeader v-else use-resizer class="body__table__header" @resizer="(e) => onMousedownResizer(e, header)">
                <div class="body__table__header__inner">
                  {{ vm.convertHeaderKeyToDisplayValue(header) }}
                  <ArrowIcon
                    :asc="convertHeaderKeyToSortKey(header) === sortTarget ? sortType === 'ASC' : false"
                    @on-click="() => onClickSortArrowHandler(header)"
                  />
                </div>
              </EzHeader>
            </template>
            <EzHeader sticky="right">
              <TableHeaderModifyButton :modifiable-header-list="modifiableHeaders" @toggle="onToggleHeader" />
            </EzHeader>
          </template>
          <template #body>
            <EzTableRow
              v-for="(data, index1) in pageType === PageType.ForecastAccuracy ? DetailBaseBusinessData : DetailAggregateBusinessData"
              :key="index1"
              :grid-template-columns="gridTemplateColumns"
            >
              <template v-for="(header, index2) in headerList.slice(0, -1)" :key="index2">
                <EzCell
                  v-if="header === 'checkbox'"
                  :is-highlighted="checkedBusinessId.includes(data.businessId)"
                  class="body__table__cell"
                >
                  <RowCheckBox @change-status="(isChecked) => onCheckedRow(data.businessId, isChecked)" />
                </EzCell>
                <EzCell
                  v-else-if="header === 'detail'"
                  :content-padding="0"
                  :is-highlighted="checkedBusinessId.includes(data.businessId)"
                  class="body__table__cell"
                >
                  <RouterLink
                    v-if="data.businessId"
                    :to="
                      isBaseBusiness
                        ? { name: 'customer-forecast-timeline', params: { defaultBusinessId: data.businessId } }
                        : { name: 'recommend-order', params: { id: data.businessId } }
                    "
                    target="_blank"
                  >
                    <TextButton class="table-detail-button" button-style="btn-secondary" :on-click="() => {}">
                      {{ t('詳細') }}
                    </TextButton>
                  </RouterLink>
                </EzCell>
                <EzCell
                  v-else-if="
                    ['aggregateBusinessResponsibleUsers', 'baseBusinessResponsibleUsers', 'forecastForReceiptResponsibleUsers'].includes(
                      header
                    )
                  "
                  :is-highlighted="checkedBusinessId.includes(data.businessId)"
                  class="body__table__cell"
                >
                  {{ vm.convertUserAccountToDisplayValue(data[header]) }}
                </EzCell>
                <EzCell
                  v-else-if="vm.numberHeaderKeys.includes(header)"
                  is-number
                  :is-highlighted="checkedBusinessId.includes(data.businessId)"
                  class="body__table__cell"
                >
                  {{ vm.convertNumberStringToDisplayValue(header, data[header]) }}
                </EzCell>
                <EzCell v-else :is-highlighted="checkedBusinessId.includes(data.businessId)" class="body__table__cell">
                  {{ data[header] ?? '--' }}
                </EzCell>
              </template>
              <EzCell />
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </div>
  </InventoryDashboardChildLayout>
</template>
<style lang="scss" scoped>
.body {
  display: grid;
  padding: 8px 16px 24px 16px;
  border-radius: 4px;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  gap: 16px;
  min-width: 0;
  overflow: hidden;

  &__header {
    display: grid;
    padding: 8px 0;
    gap: 8px;
    color: $textDefault;
    &__title {
      display: grid;
      height: 24px;
      grid-template-columns: repeat(2, max-content);
      align-items: center;
      gap: 8px;
      &--text {
        font-weight: 700;
        font-size: 16px;
        line-height: 1;
      }
    }
    &__desc {
      display: grid;
      gap: 8px;
      font-weight: 400;
      font-size: 14px;
      line-height: 1.3;
    }
    &__control {
      display: grid;
      grid-template-columns: repeat(2, max-content);
      justify-content: space-between;
      &__filter {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, max-content));
        gap: 16px;
        align-items: center;
        &--month-picker {
          display: grid;
          grid-template-columns: repeat(2, max-content);
          gap: 4px;
          align-items: center;
          &__input {
            display: grid;
            width: max-content;
            height: 32px;
            border: 1px solid $borderDefault;
            border-radius: 4px;
            align-items: center;
            width: 115px;
          }
        }
        &--checkbox {
          display: grid;
          grid-template-columns: repeat(2, max-content);
          gap: 4px;
          align-items: center;
          user-select: none;
        }
      }
    }
  }
  &__table {
    overflow-x: auto;
    :deep(.table__header-container) {
      width: 100%;
    }
    &__container {
      position: relative;
    }
    &__header {
      width: 100%;
      height: 36px;
      font-weight: 700;
      font-size: 14px;
      line-height: 1;
      &__inner {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(2, max-content);
        justify-content: space-between;
        align-items: center;
      }
    }
    &__cell {
      height: 24px;
      font-weight: 400;
      font-size: 12px;
      line-height: 1;
    }
  }
}

.table-detail-button {
  display: grid;
  justify-content: center;
  align-items: center;
  font-family: Noto Sans JP;
  padding: unset;
  height: 16px;
  width: 56px;
  background-color: $semanticWhite;
  color: $semanticPrimary;
  border: 1px solid $semanticPrimary;
  font-size: 12px;
  line-height: 12px;
  font-weight: 700;
}

a {
  &:hover,
  &:focus,
  &:focus-visible {
    color: inherit !important;
    background-color: inherit !important;
  }
}
</style>
<style lang="scss">
.body__header__control__filter--month-picker__input input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
  text-align: center;
}
</style>
